import { createLogger } from '../../../../../../shared/helpers/loggerFacade';
import type { LayoutSnapshot } from './types';

const logger = createLogger('room-agent-recorder');

let wasTrackerErrorLoggedAt = 0;
const TRACKER_ERROR_LOG_COOLDOWN_MS = 60_000;

/**
 * Таймаут для детекции замороженных потоков (мс).
 * Если rVFC callback не сработал за это время, видео помечается dirty и callback перерегистрируется.
 */
export const FROZEN_TIMEOUT_MS = 2000;

/**
 * Отслеживание «грязных» видео-элементов через requestVideoFrameCallback.
 *
 * Регистрирует rVFC callback на каждом <video> элементе с активным MediaStream,
 * помечает видео как dirty при получении нового кадра, и предоставляет
 * атомарный consume для цикла отрисовки.
 *
 * MutationObserver отслеживает добавление/удаление видео из DOM.
 * Frozen-stream detection перерегистрирует callback при отсутствии кадров.
 */
export class VideoFrameTracker {
    private _dirtySet: Set<HTMLVideoElement> = new Set();
    private _callbackHandles: Map<HTMLVideoElement, number> = new Map();
    private _frozenTimers: Map<HTMLVideoElement, ReturnType<typeof setTimeout>> = new Map();
    private _observer: MutationObserver | undefined;
    private _eventCleanups: Map<HTMLVideoElement, () => void> = new Map();
    private _enabled: boolean;
    private _onUntrack?: (video: HTMLVideoElement) => void;
    private _prevLayout: LayoutSnapshot = {
        participantCount: -1,
        activeGridView: '',
        currentPage: -1,
        isScreenSharingActive: false,
    };

    constructor(enabled: boolean, onUntrack?: (video: HTMLVideoElement) => void) {
        this._enabled = enabled;
        this._onUntrack = onUntrack;

        if (enabled) {
            this._start();
        }
    }

    private _start(): void {
        // Сканируем существующие видео в DOM
        document.querySelectorAll<HTMLVideoElement>('video').forEach((video) => {
            if (video.srcObject instanceof MediaStream) {
                this._trackVideo(video);
            }
        });

        // Наблюдаем за добавлением/удалением видео
        this._observer = new MutationObserver(this._handleMutations);
        const root = document.querySelector('[data-video-root]') ?? document.body;
        this._observer.observe(root, { childList: true, subtree: true });
    }

    private _trackVideo(video: HTMLVideoElement): void {
        if (this._callbackHandles.has(video)) {
            return;
        }

        // Первый кадр всегда рисуется
        this._dirtySet.add(video);
        this._registerCallback(video);
        this._startFrozenTimer(video);

        // Обработчики событий для force-dirty при смене потока
        const onVideoEvent = () => {
            this._onVideoEvent(video);
        };

        video.addEventListener('loadeddata', onVideoEvent);
        video.addEventListener('resize', onVideoEvent);

        this._eventCleanups.set(video, () => {
            video.removeEventListener('loadeddata', onVideoEvent);
            video.removeEventListener('resize', onVideoEvent);
        });
    }

    private _registerCallback(video: HTMLVideoElement): void {
        try {
            const handle = video.requestVideoFrameCallback(() => {
                this._dirtySet.add(video);
                this._resetFrozenTimer(video);
                // rVFC — one-shot, перерегистрируем
                this._registerCallback(video);
            });

            this._callbackHandles.set(video, handle);
        } catch (error) {
            // Залогируем ошибку, но не чаще раза в минуту
            const now = performance.now();

            if (!wasTrackerErrorLoggedAt || now - wasTrackerErrorLoggedAt > TRACKER_ERROR_LOG_COOLDOWN_MS) {
                wasTrackerErrorLoggedAt = now;
                logger.error(error, {
                    method: 'VideoFrameTracker._registerCallback',
                });
            }
        }
    }

    private _untrackVideo(video: HTMLVideoElement): void {
        const handle = this._callbackHandles.get(video);

        if (handle !== undefined) {
            video.cancelVideoFrameCallback(handle);
        }

        this._callbackHandles.delete(video);
        this._dirtySet.delete(video);
        this._clearFrozenTimer(video);
        this._onUntrack?.(video);

        const cleanup = this._eventCleanups.get(video);

        if (cleanup) {
            cleanup();
            this._eventCleanups.delete(video);
        }
    }

    private _handleMutations = (mutations: MutationRecord[]): void => {
        for (const mutation of mutations) {
            mutation.addedNodes.forEach((node) => {
                if (node instanceof HTMLVideoElement && node.srcObject instanceof MediaStream) {
                    this._trackVideo(node);
                }

                if (node instanceof Element) {
                    node.querySelectorAll<HTMLVideoElement>('video').forEach((v) => {
                        if (v.srcObject instanceof MediaStream) {
                            this._trackVideo(v);
                        }
                    });
                }
            });

            mutation.removedNodes.forEach((node) => {
                if (node instanceof HTMLVideoElement) {
                    this._untrackVideo(node);
                }

                if (node instanceof Element) {
                    node.querySelectorAll<HTMLVideoElement>('video').forEach((v) => {
                        this._untrackVideo(v);
                    });
                }
            });
        }
    };

    private _startFrozenTimer(video: HTMLVideoElement): void {
        this._clearFrozenTimer(video);

        this._frozenTimers.set(
            video,
            setTimeout(() => {
                // Замороженный поток: force-mark dirty, перерегистрируем callback
                this._dirtySet.add(video);
                this._registerCallback(video);
                this._startFrozenTimer(video);
            }, FROZEN_TIMEOUT_MS)
        );
    }

    private _resetFrozenTimer(video: HTMLVideoElement): void {
        this._clearFrozenTimer(video);
        this._startFrozenTimer(video);
    }

    private _clearFrozenTimer(video: HTMLVideoElement): void {
        const timer = this._frozenTimers.get(video);

        if (timer) {
            clearTimeout(timer);
            this._frozenTimers.delete(video);
        }
    }

    private _onVideoEvent(video: HTMLVideoElement): void {
        this._dirtySet.add(video);

        // Отменяем текущий callback и перерегистрируем — цепочка должна быть свежей
        const handle = this._callbackHandles.get(video);

        if (handle !== undefined) {
            video.cancelVideoFrameCallback(handle);
            this._callbackHandles.delete(video);
        }

        this._registerCallback(video);
    }

    /** Есть ли видео с новыми кадрами */
    public hasDirty(): boolean {
        return this._dirtySet.size > 0;
    }

    /**
     * Атомарно возвращает множество dirty-видео и очищает его.
     * Гарантирует, что ни один кадр не потеряется между чтением и очисткой.
     */
    public consumeDirty(): Set<HTMLVideoElement> {
        const snapshot = new Set(this._dirtySet);

        this._dirtySet.clear();

        return snapshot;
    }

    /**
     * Проверяет, изменилась ли раскладка, и помечает все видео dirty при изменении.
     * Вызывается каждый кадр из drawingFramesSaga.
     */
    public checkLayoutChange(
        participantCount: number,
        activeGridView: string,
        currentPage: number,
        isScreenSharingActive: boolean
    ): void {
        const changed =
            participantCount !== this._prevLayout.participantCount ||
            activeGridView !== this._prevLayout.activeGridView ||
            currentPage !== this._prevLayout.currentPage ||
            isScreenSharingActive !== this._prevLayout.isScreenSharingActive;

        if (changed) {
            this._prevLayout = { participantCount, activeGridView, currentPage, isScreenSharingActive };
            this.markAllDirty();
        }
    }

    /**
     * Помечает все отслеживаемые видео как dirty.
     * Используется при смене layout для принудительной полной перерисовки.
     */
    public markAllDirty(): void {
        for (const video of this._callbackHandles.keys()) {
            this._dirtySet.add(video);
        }
    }

    /** Количество отслеживаемых видео-элементов */
    public trackedCount(): number {
        return this._callbackHandles.size;
    }

    /** Очистка всех ресурсов: disconnect observer, cancel rVFC, clear timers */
    public destroy(): void {
        if (this._observer) {
            this._observer.disconnect();
        }

        for (const [video, handle] of this._callbackHandles) {
            video.cancelVideoFrameCallback(handle);
        }

        for (const timer of this._frozenTimers.values()) {
            clearTimeout(timer);
        }

        for (const cleanup of this._eventCleanups.values()) {
            cleanup();
        }

        this._callbackHandles.clear();
        this._dirtySet.clear();
        this._frozenTimers.clear();
        this._eventCleanups.clear();
    }
}
